import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import useStore from '../../../store/useStore';

/**
 * NotifyMeButton
 *
 * Shows a "Notify Me" / "Remove Alert" button when a product is out of stock.
 * Calls the Notify Me API to register / unregister the customer.
 *
 * Props:
 *   productId        — required (number | string)
 *   variantId        — required (number | string)
 *   pincode          — optional string (6-digit pincode)
 *   isAuthenticated  — bool
 *   openAuthModal    — function to open login modal
 *   className        — optional extra classes
 */
const NotifyMeButton = ({
  productId,
  variantId,
  pincode,
  isAuthenticated,
  openAuthModal,
  className = '',
}) => {
  const {
    notifyMeStatus,
    fetchNotifyMeStatus,
    registerNotifyMe,
    removeNotifyMe,
  } = useStore();

  const key = `${productId}_${variantId}`;
  const status = notifyMeStatus[key] ?? 'idle'; // 'idle' | 'loading' | 'registered' | 'error'

  // Fetch current status once when component mounts and user is authenticated
  const hasFetched = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !productId || !variantId) return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchNotifyMeStatus({ product_id: productId, product_variant_id: variantId, pincode });
  }, [isAuthenticated, productId, variantId, pincode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch if variant changes
  useEffect(() => {
    if (!isAuthenticated || !productId || !variantId) return;
    hasFetched.current = false;
    fetchNotifyMeStatus({ product_id: productId, product_variant_id: variantId, pincode });
    hasFetched.current = true;
  }, [productId, variantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = status === 'loading';
  const isRegistered = status === 'registered';

  const handleClick = async () => {
    if (!isAuthenticated) {
      toast('Please login to get stock alerts', { icon: '🔒', id: 'auth-notify-me' });
      if (openAuthModal) openAuthModal();
      return;
    }

    if (isRegistered) {
      const res = await removeNotifyMe({
        product_id: productId,
        product_variant_id: variantId,
      });
      if (res.success) {
        toast.success('Stock alert removed.', { id: 'notify-me-remove' });
      } else {
        toast.error(res.message || 'Failed to remove alert.', { id: 'notify-me-remove-err' });
      }
    } else {
      const res = await registerNotifyMe({
        product_id: productId,
        product_variant_id: variantId,
        pincode,
      });
      if (res.success) {
        toast.success("🔔 You'll be notified when it's back in stock!", {
          id: 'notify-me-success',
          duration: 4000,
        });
      } else {
        toast.error(res.message || 'Could not register. Try again.', {
          id: 'notify-me-error',
        });
      }
    }
  };

  return (
    <button
      id={`notify-me-btn-${productId}-${variantId}`}
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className={`
        relative flex items-center justify-center gap-2.5
        min-w-[160px] rounded-2xl px-6 py-2.5 text-base font-bold shadow-sm
        transition-all duration-200 active:scale-[0.97]
        disabled:cursor-not-allowed disabled:opacity-60
        sm:min-w-[180px] sm:py-3.5 sm:text-lg
        ${
          isRegistered
            ? 'border-2 border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'
            : 'border-2 border-violet-500 bg-white text-violet-700 hover:bg-violet-50'
        }
        ${className}
      `}
      aria-label={isRegistered ? 'Remove stock alert' : 'Notify me when back in stock'}
    >
      {isLoading ? (
        /* Spinner */
        <svg
          className="h-5 w-5 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12" cy="12" r="10"
            stroke="currentColor" strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
          />
        </svg>
      ) : isRegistered ? (
        /* Bell-slash icon */
        <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M9.172 9.172A4 4 0 0115 12v3m-6.586-6.586A4 4 0 008 12v3l-2 2h10M3 3l18 18M10 17a2 2 0 104 0" />
        </svg>
      ) : (
        /* Bell icon */
        <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.437L4 17h5m6 0a3 3 0 11-6 0" />
        </svg>
      )}
      <span>
        {isLoading
          ? 'Checking…'
          : isRegistered
          ? 'Remove Alert'
          : 'Notify Me'}
      </span>
    </button>
  );
};

export default NotifyMeButton;
